import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
} from "@devhop/ui";
import { FileText } from "lucide-react";
import type { CompanyPolicyVersionListItem } from "@/modules/hr/company-policy/domain/types";
import { usePolicyVersionsQuery } from "../api/queries";

function formatDate(s: string) {
  try {
    return new Date(s).toLocaleDateString("lo-LA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

type PolicyVersionsListProps = {
  policyId: string;
  limit?: number;
};

export function PolicyVersionsList({ policyId, limit = 5 }: PolicyVersionsListProps) {
  const [viewingVersion, setViewingVersion] =
    useState<CompanyPolicyVersionListItem | null>(null);
  const { data, isLoading } = usePolicyVersionsQuery(policyId, {
    offset: 0,
    limit,
  });

  const versions = data?.data ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">ປະຫວັດສະບັບ</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        </CardContent>
      </Card>
    );
  }

  if (versions.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">ປະຫວັດສະບັບ</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີສະບັບ</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">ປະຫວັດສະບັບ</CardTitle>
        <p className="text-muted-foreground text-sm">
          ສະບັບລ່າສຸດ {versions.length} ລາຍການ
        </p>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {versions.map((v) => (
            <li
              key={v.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2 text-sm"
            >
              <span>
                ສະບັບ {v.version} — ມີຜົນຕັ້ງແຕ່ {formatDate(v.effectiveFrom)}
                {v.effectiveTo ? ` ເຖິງ ${formatDate(v.effectiveTo)}` : ""}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setViewingVersion(v)}
                  aria-label="ເບິ່ງເນື້ອໃນ"
                >
                  <FileText className="h-4 w-4" />
                  ເບິ່ງເນື້ອໃນ
                </Button>
                <span className="text-muted-foreground text-xs">
                  {formatDate(v.createdAt)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>

      <Modal
        open={!!viewingVersion}
        onOpenChange={(open) => !open && setViewingVersion(null)}
        title={
          viewingVersion
            ? `ເນື້ອໃນສະບັບ ${viewingVersion.version}`
            : ""
        }
        description={
          viewingVersion
            ? `ມີຜົນຕັ້ງແຕ່ ${formatDate(viewingVersion.effectiveFrom)}${
                viewingVersion.effectiveTo
                  ? ` — ສິ້ນສຸດ ${formatDate(viewingVersion.effectiveTo)}`
                  : ""
              }`
            : ""
        }
        size="lg"
      >
        {viewingVersion && (
          <div className="space-y-3">
            {viewingVersion.fileKey && !viewingVersion.content && (
              <p className="text-muted-foreground text-sm">
                ເນື້ອໃນເກັບໃນໄຟລ໌ (file key: {viewingVersion.fileKey})
              </p>
            )}
            {viewingVersion.content ? (
              <pre className="overflow-auto max-h-[60vh] rounded-md border bg-muted/30 p-4 font-mono text-sm whitespace-pre-wrap">
                {viewingVersion.content}
              </pre>
            ) : !viewingVersion.fileKey ? (
              <p className="text-muted-foreground text-sm">ບໍ່ມີເນື້ອໃນ</p>
            ) : null}
          </div>
        )}
      </Modal>
    </Card>
  );
}
